<template>
  <article v-if="page && Object.keys(page).length" :class="$style.pyv">
    <header>
      <PostFeaturedImage
        v-if="page.featuredImage"
        :featured-image="page.featuredImage"
        :srcset="page.imageSrcset"
        :ga4-data="
          JSON.stringify({
            click_type: 'hero',
            component: 'PlanYourVisit',
            content_group: 'page',
            gtm_tag: 'image',
            file_name: `${page.featuredImage.sourceUrl.split('/').pop()}`,
          })
        "
        :is-wide="page.featuredImageWidth === 'wide'"
      ></PostFeaturedImage>
    </header>
    <SecondaryNav v-if="page.secondary_nav" :items="page.secondary_nav"></SecondaryNav>
    <div :class="$style.grid">
      <h1 :class="[$style.title, 'no-border-above']">{{ page.title }}</h1>
      <section :class="[$style.grid, $style.section]">
        <Hours :id="page.hours.anchor" :class="$style.hours" :heading="page.hours.heading"></Hours>
        <div class="span-seven">
          <h3 v-if="page.hours.regular_hours_label" class="heading-4">
            {{ page.hours.regular_hours_label }}
          </h3>
          <div
            v-if="page.hours.regular_hours"
            :class="$style.unspaced"
            v-html="page.hours.regular_hours"
          ></div>
          <h3 v-if="page.hours.holiday_hours_label" class="heading-4">
            {{ page.hours.holiday_hours_label }}
          </h3>
          <div
            v-if="page.hours.holiday_hours"
            :class="$style.unspaced"
            v-html="page.hours.holiday_hours"
          ></div>
          <h3 v-if="page.hours.special_hours_label" class="heading-4">
            {{ page.hours.special_hours_label }}
          </h3>
          <div
            v-if="page.hours.special_hours"
            :class="$style.unspaced"
            v-html="page.hours.special_hours"
          ></div>
        </div>
      </section>
      <section :class="[$style.directions, $style.grid, $style.section]">
        <h2 v-if="page.directions.heading" :id="page.directions.anchor" :class="$style.title">
          {{ page.directions.heading }}
        </h2>
        <div :class="[$style.column, $style.unspaced]">
          <h3 v-if="page.directions.address.subheading" class="heading-4">
            {{ page.directions.address.subheading }}
          </h3>
          <p v-if="page.directions.address.street" v-html="page.directions.address.street"></p>
          <PreparedAnchor
            v-if="page.directions.address.map_link && page.directions.address.map_image.sourceUrl"
            :class="[$style.map, 'aspect-container']"
            :to="page.directions.address.map_link"
            :behavior="page.directions.address.map_link_behavior"
          >
            <picture>
              <img
                :alt="page.directions.address.map_image.altText"
                :src="page.directions.address.map_image.sourceUrl"
                :srcset="page.directions.address.map_image.imageSrcset"
                class="aspect-image"
                loading="lazy"
                sizes="(min-width: 960px) 640px, 100vw"
              />
            </picture>
          </PreparedAnchor>
          <picture
            v-if="!page.directions.address.map_link && page.directions.address.map_image.sourceUrl"
            class="aspect-container"
          >
            <img
              :alt="page.directions.address.map_image.altText"
              :src="page.directions.address.map_image.sourceUrl"
              :srcset="page.directions.address.map_image.imageSrcset"
              class="aspect-image"
              loading="lazy"
              sizes="(min-width: 960px) 640px, 100vw"
            />
          </picture>
          <PreparedAnchor
            v-if="page.directions.address.map_link"
            class="more-link"
            :to="page.directions.address.map_link"
            :behavior="page.directions.address.map_link_behavior"
          >
            {{ page.directions.address.map_link_text }}
          </PreparedAnchor>
        </div>
        <div :class="[$style.column, $style.unspaced]">
          <h3 v-if="page.directions.transit.subheading" class="heading-4">
            {{ page.directions.transit.subheading }}
          </h3>
          <dl>
            <span v-for="(item, index) in page.directions.transit.directions" :key="index">
              <dt>{{ item.title }}</dt>
              <dd v-html="item.direction"></dd>
            </span>
          </dl>
          <PreparedAnchor
            v-if="page.directions.parking_link"
            class="more-link"
            :to="page.directions.parking_link"
            :behavior="page.directions.parking_link_behavior"
          >
            {{ page.directions.parking_link_text }}
          </PreparedAnchor>
        </div>
      </section>
      <section :class="[$style.admissions, $style.grid, $style.section]">
        <h2 v-if="page.admission.heading" :id="page.admission.anchor" :class="$style.title">
          {{ page.admission.heading }}
        </h2>
        <dl
          v-if="page.admission.column_one && Object.keys(page.admission.column_one).length"
          class="span-six"
        >
          <div v-for="(row, key) in page.admission.column_one" :key="key">
            <dt>{{ row.text }}</dt>
            <dd>{{ row.value }}</dd>
          </div>
        </dl>
        <dl
          v-if="page.admission.column_two && Object.keys(page.admission.column_two).length"
          class="span-six"
        >
          <div v-for="(row, key) in page.admission.column_two" :key="key">
            <dt>{{ row.text }}</dt>
            <dd>{{ row.value }}</dd>
          </div>
        </dl>
        <div
          v-if="page.admission.button_link || page.admission.cta_text || page.admission.cta_link"
          :class="$style.cta"
        >
          <div v-if="page.admission.cta_text" v-html="page.admission.cta_text"></div>
          <PreparedAnchor
            v-if="page.admission.cta_link"
            class="more-link"
            :to="page.admission.cta_link"
            :behavior="page.admission.cta_link_behavior"
          >
            {{ page.admission.cta_link_text }}
          </PreparedAnchor>
          <PreparedAnchor
            v-if="page.admission.button_link"
            :class="['button-primary', $style.center]"
            :to="page.admission.button_link"
            :behavior="page.admission.button_link_behavior"
          >
            {{ page.admission.button_link_text }}
          </PreparedAnchor>
        </div>
      </section>
      <section
        v-for="(block, key) in page.blocks"
        :key="key"
        :class="[{ [$style.section]: block.heading }, $style.grid]"
      >
        <h2 v-if="block.heading" :id="block.anchor" :class="$style.title">
          {{ block.heading }}
        </h2>
        <div :class="$style[block.type]">
          <PreparedAnchor
            v-if="block.column_one_link && block.column_one_image.sourceUrl"
            class="aspect-container"
            :to="block.column_one_link"
            :behavior="block.column_one_link_behavior"
          >
            <picture>
              <img
                :alt="block.column_one_image.altText"
                :sizes="
                  block.type === 'one-up'
                    ? '(min-width: 960px) 1280px, 100vw'
                    : '(min-width: 960px) 640px, 100vw'
                "
                :src="block.column_one_image.sourceUrl"
                :srcset="block.column_one_image.imageSrcset"
                class="aspect-image"
                loading="lazy"
              />
            </picture>
          </PreparedAnchor>
          <picture
            v-if="!block.column_one_link && block.column_one_image.sourceUrl"
            class="aspect-container"
          >
            <img
              :alt="block.column_one_image.altText"
              :sizes="
                block.type === 'one-up'
                  ? '(min-width: 960px) 1280px, 100vw'
                  : '(min-width: 960px) 640px, 100vw'
              "
              :src="block.column_one_image.sourceUrl"
              :srcset="block.column_one_image.imageSrcset"
              class="aspect-image"
              loading="lazy"
            />
          </picture>
          <h3 v-if="block.column_one_subheading" class="heading-4">
            {{ block.column_one_subheading }}
          </h3>
          <div v-if="block.column_one_content" v-html="block.column_one_content"></div>
          <PreparedAnchor
            v-if="block.column_one_link"
            class="more-link"
            :to="block.column_one_link"
            :behavior="block.column_one_link_behavior"
          >
            {{ block.column_one_link_text }}
          </PreparedAnchor>
        </div>
        <div v-if="block.type === 'two-up'" :class="$style[block.type]">
          <PreparedAnchor
            v-if="block.column_two_link && block.column_two_image.sourceUrl"
            class="aspect-container"
            :to="block.column_two_link"
            :behavior="block.column_two_link_behavior"
          >
            <picture>
              <img
                :alt="block.column_two_image.altText"
                :src="block.column_two_image.sourceUrl"
                :srcset="block.column_two_image.imageSrcset"
                class="aspect-image"
                loading="lazy"
                sizes="(min-width: 960px) 640px, 100vw"
              />
            </picture>
          </PreparedAnchor>
          <picture
            v-if="!block.column_two_link && block.column_two_image.sourceUrl"
            class="aspect-container"
          >
            <img
              :alt="block.column_two_image.altText"
              :src="block.column_two_image.sourceUrl"
              :srcset="block.column_two_image.imageSrcset"
              class="aspect-image"
              loading="lazy"
              sizes="(min-width: 960px) 640px, 100vw"
            />
          </picture>
          <h3 v-if="block.column_two_subheading" class="heading-4">
            {{ block.column_two_subheading }}
          </h3>
          <div v-if="block.column_two_content" v-html="block.column_two_content"></div>
          <PreparedAnchor
            v-if="block.column_two_link"
            class="more-link"
            :to="block.column_two_link"
            :behavior="block.column_two_link_behavior"
          >
            {{ block.column_two_link_text }}
          </PreparedAnchor>
        </div>
      </section>
    </div>
  </article>
</template>

<script setup lang="ts">
import { BootStrap } from '@/types/global.interface';
import { dataLoaded, useHandleError, useMetaData } from '@/composables/Common.js';
import gql from 'graphql-tag';
import Hours from '@/components/common/Hours.vue';
import { Image, SecondaryNavItem } from '@/types/common.interface';
import { inject, ref } from 'vue';
import { planYourVisitQuery } from '@/queries/PlanYourVisitQuery.js';
import PostFeaturedImage from '@/components/common/PostFeaturedImage.vue';
import PreparedAnchor from '@/components/common/PreparedAnchor.vue';
import SecondaryNav from '@/components/navigation/SecondaryNav.vue';
import { storeToRefs } from 'pinia';
import { useMainStore } from '@/stores/mainStore.js';
import { useQuery } from 'villus';

interface Page {
  blocks: Array<{
    anchor: string;
    column_one_image: Image;
    column_one_content: Image;
    column_one_link: string;
    column_one_link_behavior: string;
    column_one_link_text: string;
    column_one_subheading: string;
    column_two_image: Image;
    column_two_content: Image;
    column_two_link: string;
    column_two_link_behavior: string;
    column_two_link_text: string;
    column_two_subheading: string;
    heading: string;
    type: string;
  }>;
  excerpt: string;
  featuredImage: Image;
  imageSrcset: string;
  admission: {
    anchor: string;
    button_link: string;
    button_link_behavior: string;
    button_link_text: string;
    cta_link: string;
    cta_link_behavior: string;
    cta_link_text: string;
    full_text: string;
    heading: string;
    subheading: string;
    column_one: Array<{
      text: string;
      value: string;
    }>;
    column_two: Array<{
      text: string;
      value: string;
    }>;
  };
  directions: {
    address: {
      map_image: Image;
      map_link: string;
      map_link_behavior: string;
      map_link_text: string;
      subheading: string;
      street: string;
    };
    anchor: string;
    heading: string;
    parking_link: string;
    parking_link_behavior: string;
    parking_link_text: string;
    transit: {
      subheading: string;
      directions: Array<{
        direction: string;
        title: string;
      }>;
    };
  };
  hours: {
    anchor: string;
    heading: string;
    holiday_hours: string;
    holiday_hours_label: string;
    regular_hours: string;
    regular_hours_label: string;
    special_hours: string;
    special_hours_label: string;
  };
  secondary_nav: Array<SecondaryNavItem>;
  title: string;
  video: string;
}

const bootstrap = inject<BootStrap>('bootstrap');

const store = useMainStore();
const { useBootstrap } = storeToRefs(store);

const { handleError } = useHandleError();
const { setMetaDescription, setMetaTitle } = useMetaData();

const page = ref<Page>();
const shouldSkip = ref(true);

if (useBootstrap.value) {
  shouldSkip.value = true;
  page.value = bootstrap.initial.main.posts.planYourVisit;
  setMetaTitle('Plan Your Visit');
  setMetaDescription(bootstrap.initial.main.posts.planYourVisit.excerpt);
  dataLoaded();
}
if (!useBootstrap.value) {
  shouldSkip.value = false;
}

const { data, error } = useQuery({
  onData: (data) => {
    if (data && Object.keys(data).length) {
      page.value = data.planYourVisit;
      setMetaTitle(data.planYourVisit.title);
      setMetaDescription(data.planYourVisit.excerpt);
      dataLoaded();
    }
    if (data && Object.keys(data).length === 0) {
      handleError(data.planYourVisit.title, error);
    }
  },
  query: gql`
    ${planYourVisitQuery}
  `,
  skip: shouldSkip,
});
</script>

<script lang="ts">
export default {
  beforeRoute(params, villusClient) {
    return new Promise<void>((resolve, reject) => {
      villusClient
        .executeQuery({
          query: gql`
            ${planYourVisitQuery}
          `,
          variables: {},
        })
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  },
};
</script>

<style lang="scss" module>
.admissions {
  .cta {
    @include space-28-32-above;

    & {
      grid-column: span 6;
      grid-row: 3;
    }

    p:first-of-type {
      @include space-0-above;
    }
  }

  dl {
    @include body-1;
    @include space-28-32-above;

    & {
      grid-row: 2;
    }

    div {
      @include space-8-10-above;

      & {
        display: grid;
        grid-template-columns: 8fr 1fr;
      }

      &:first-of-type {
        @include space-0-above;
      }

      &:last-of-type {
        display: block;

        dt,
        dd {
          display: inline-block;
        }

        dd {
          margin-left: 1ex;
        }
      }
    }

    dt {
      @include space-0-above;
    }

    dd {
      text-align: right;
    }
  }
}

.center {
  display: block;
  margin-top: 1rem;
  width: fit-content;
}

.column {
  @include space-28-32-above;
}

.directions {
  dt {
    @include heading-4;
  }
}

dl {
  margin: 0;

  dt {
    margin-top: 0;

    &::nth-of-type(n + 2) {
      @include space-24-32-above;
    }
  }
}

.hero {
  @include hero;
}

.one-up,
.two-up {
  @include space-28-32-above;

  h2,
  h3 {
    & + div {
      p {
        &:first-of-type {
          @include space-0-above;
        }
      }
    }
  }
}

.pyv {
  :global(.aspect-container) {
    @include space-0-above;

    &.map {
      @include space-24-32-above;
    }
  }

  dd {
    margin: 0;

    p {
      margin: 0;
    }
  }
}

.section {
  h2:first-of-type {
    margin-top: 0;
  }

  &:not(:first-of-type) {
    @include space-48-64-above;

    &::before {
      @include line;

      & {
        margin-top: -1.5rem;
      }
    }
  }

  @media (min-width: $break-point) {
    &:not(:first-of-type) {
      &::before {
        margin-top: -2rem;
      }
    }
  }
}

.td {
  text-align: right;
}

.unspaced {
  p,
  h3:first-of-type,
  h4:first-of-type {
    margin-top: 0;
  }
}

.video {
  background: none;
  margin: 2rem auto 0 auto;

  iframe {
    height: 100%;
    position: absolute;
    width: 100%;
  }
}

@media (min-width: $break-point) {
  .blocks,
  .section {
    grid-column: 1 / span 8;
  }

  .grid {
    @include grid;

    .grid {
      grid-column: span 12;
    }
  }

  .column {
    grid-column: span 6;
  }

  .directions {
    dl > span:nth-child(1) > dt {
      @include space-0-above;
    }
  }

  .hours,
  .one-up {
    grid-column: span 12;
  }

  .title {
    grid-column: span 12;
  }

  .two-up {
    grid-column: span 6;
  }
}
</style>
